import React from 'react';
import { Info, DollarSign, Calculator, Scale, Briefcase, AlertTriangle, Book, Award, FileSearch } from 'lucide-react';

/**
 * Компонент с информацией о государственной пошлине и судебных расходах
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о судебных расходах: основная информация
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-indigo-500" />
          Что такое государственная пошлина?
        </h3>
        <p>
          <strong>Государственная пошлина</strong> — это сбор, взимаемый с физических и юридических лиц при их обращении в государственные органы, 
          включая суды, за совершение юридически значимых действий. Госпошлина является одним из видов судебных расходов и регулируется 
          главой 25.3 Налогового кодекса РФ.
        </p>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Виды судебных расходов
        </h3>
        <p>
          Согласно статье 88 ГПК РФ и статье 101 АПК РФ, к судебным расходам относятся:
        </p>
        <ul>
          <li><strong>Государственная пошлина</strong> — обязательный платеж при подаче заявлений и жалоб в суд</li>
          <li><strong>Судебные издержки:</strong>
            <ul>
              <li>Расходы на оплату услуг представителей</li>
              <li>Расходы на оплату экспертов, специалистов, переводчиков</li>
              <li>Расходы на проезд и проживание сторон и третьих лиц</li>
              <li>Компенсация за потерю времени</li>
              <li>Расходы на производство осмотра на месте</li>
              <li>Почтовые расходы</li>
              <li>Иные расходы, признанные судом необходимыми</li>
            </ul>
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Как рассчитывается государственная пошлина?
        </h3>
        <p>
          Размер госпошлины зависит от нескольких факторов:
        </p>
        <ul>
          <li><strong>Тип суда</strong> — арбитражный суд или суд общей юрисдикции</li>
          <li><strong>Характер требований</strong> — имущественные или неимущественные</li>
          <li><strong>Цена иска</strong> — для имущественных требований</li>
          <li><strong>Категория дела</strong> — для некоторых категорий установлен фиксированный размер</li>
          <li><strong>Субъектный состав</strong> — для некоторых категорий лиц предусмотрены льготы</li>
        </ul>
        
        <p>
          Для имущественных исков госпошлина рассчитывается исходя из цены иска по регрессивной шкале — 
          чем выше сумма требований, тем ниже процент пошлины.
        </p>

        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800 my-6">
          <h4 className="text-indigo-700 dark:text-indigo-300 font-medium flex items-center gap-2">
            <Book className="w-4 h-4" />
            Правовое регулирование
          </h4>
          <p className="text-indigo-600 dark:text-indigo-400 text-sm mb-0">
            Размеры государственной пошлины установлены:
            <br/>• для арбитражных судов — статьей 333.21 НК РФ
            <br/>• для судов общей юрисдикции — статьей 333.19 НК РФ
            <br/>• льготы по уплате госпошлины — статьей 333.35-333.39 НК РФ
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Award className="w-5 h-5 text-orange-500" />
          Распределение судебных расходов
        </h3>
        <p>
          По общему правилу, судебные расходы возлагаются:
        </p>
        <ul>
          <li>На проигравшую сторону</li>
          <li>Пропорционально удовлетворенным требованиям, если иск удовлетворен частично</li>
        </ul>
        <p>
          Расходы на оплату услуг представителя взыскиваются в разумных пределах, которые определяются судом.
          Суд может снизить размер возмещения, если посчитает заявленные расходы чрезмерными.
        </p>

        <h3 className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-blue-500" />
          Услуги юристов
        </h3>
        <p>
          Стоимость услуг юристов и представителей в суде зависит от множества факторов:
        </p>
        <ul>
          <li>Сложность дела и объем исковых требований</li>
          <li>Опыт и квалификация юриста</li>
          <li>Регион оказания услуг</li>
          <li>Необходимые действия (подготовка документов, участие в заседаниях и т.д.)</li>
          <li>Срочность работы</li>
        </ul>
        <p>
          Стоимость может формироваться по почасовой ставке, фиксированной сумме за весь процесс или 
          комбинированно (фиксированная часть + процент от взысканной суммы).
        </p>

        <h3 className="flex items-center gap-2">
          <FileSearch className="w-5 h-5 text-purple-500" />
          Судебная экспертиза
        </h3>
        <p>
          Судебная экспертиза назначается судом по ходатайству стороны или по собственной инициативе, когда 
          требуются специальные знания в различных областях. Стоимость экспертизы зависит от:
        </p>
        <ul>
          <li>Вида экспертизы (строительная, товароведческая, почерковедческая и др.)</li>
          <li>Сложности вопросов и объема исследуемых материалов</li>
          <li>Срочности проведения</li>
          <li>Репутации экспертной организации</li>
        </ul>
        <p>
          Первоначально расходы на экспертизу несет сторона, заявившая соответствующее ходатайство.
          В дальнейшем эти расходы могут быть перераспределены судом согласно общим правилам 
          возмещения судебных расходов.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важные нюансы при оплате госпошлины
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Госпошлина должна быть оплачена до подачи искового заявления</li>
            <li>При увеличении исковых требований в процессе рассмотрения дела необходимо доплатить пошлину</li>
            <li>При заключении мирового соглашения до принятия решения истцу возвращается 50% уплаченной пошлины</li>
            <li>Отказ от иска до принятия решения позволяет вернуть до 70% госпошлины</li>
            <li>При оставлении заявления без рассмотрения или возвращении в большинстве случаев госпошлина подлежит возврату</li>
          </ul>
        </div>

        <h3>Преимущества квалифицированной юридической помощи</h3>
        <ul>
          <li>Профессиональная оценка перспектив дела</li>
          <li>Грамотное составление процессуальных документов</li>
          <li>Эффективное представление интересов в суде</li>
          <li>Сбор и подготовка необходимых доказательств</li>
          <li>Знание судебной практики и процессуальных нюансов</li>
          <li>Возможное снижение временных и финансовых затрат</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам оценить предстоящие расходы при обращении в суд и спланировать бюджет на судебный процесс!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;